import * as Slider from "@radix-ui/react-slider";
import classNames from "classnames";
import { useState } from "react";
import KuikaInput from "../kuika-input/kuika-input";
import "./kuika-slider.scss";

export interface KuikaSliderProps {
  value?: number;
  defaultValue?: number;
  onChange?: (value: number) => void;
  onAfterChange?: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  precision?: number;
  unit?: string;
  showInput?: boolean;
  disabled?: boolean;
  size?: "sm" | "md" | "lg";
  className?: string;
  dataTestId?: string;
}

const DEFAULT_MIN: number = 0;
const DEFAULT_MAX: number = 100;
const DEFAULT_STEP: number = 1;

const clamp = (raw: number, min: number, max: number): number => {
  if (Number.isNaN(raw)) return min;
  if (raw < min) return min;
  if (raw > max) return max;
  return raw;
};

const roundToPrecision = (raw: number, precision?: number): number => {
  if (precision === undefined || precision < 0) return raw;
  const factor: number = 10 ** precision;
  return Math.round(raw * factor) / factor;
};

const KuikaSlider = ({
  value: controlledValue,
  defaultValue,
  onChange,
  onAfterChange,
  min = DEFAULT_MIN,
  max = DEFAULT_MAX,
  step = DEFAULT_STEP,
  precision,
  unit,
  showInput = true,
  disabled = false,
  size = "md",
  className,
  dataTestId
}: KuikaSliderProps): JSX.Element => {
  const [internalValue, setInternalValue] = useState<number>(defaultValue ?? min);
  const isControlled: boolean = controlledValue !== undefined;
  const current: number = isControlled ? (controlledValue as number) : internalValue;

  const handleValueChange = (next: number[]): void => {
    const nextValue: number = roundToPrecision(clamp(next[0], min, max), precision);
    if (!isControlled) {
      setInternalValue(nextValue);
    }
    if (onChange) {
      onChange(nextValue);
    }
  };

  const handleValueCommit = (next: number[]): void => {
    if (!onAfterChange) return;
    const committed: number = roundToPrecision(clamp(next[0], min, max), precision);
    onAfterChange(committed);
  };

  const handleInputChange = (
    _event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    raw: string | number
  ): void => {
    const parsed: number = typeof raw === "number" ? raw : Number.parseFloat(raw);
    if (Number.isNaN(parsed)) return;
    const nextValue: number = roundToPrecision(clamp(parsed, min, max), precision);
    if (!isControlled) {
      setInternalValue(nextValue);
    }
    if (onChange) {
      onChange(nextValue);
    }
    if (onAfterChange) {
      onAfterChange(nextValue);
    }
  };

  const wrapperClassName: string = classNames(
    "kuika-slider",
    `kuika-slider--${size}`,
    {
      "kuika-slider--disabled": disabled,
      "kuika-slider--with-input": showInput
    },
    className
  );

  return (
    <div className={wrapperClassName} data-testid={dataTestId}>
      <Slider.Root
        className="kuika-slider__root"
        value={[current]}
        onValueChange={handleValueChange}
        onValueCommit={handleValueCommit}
        min={min}
        max={max}
        step={step}
        disabled={disabled}
      >
        <Slider.Track className="kuika-slider__track">
          <Slider.Range className="kuika-slider__range" />
        </Slider.Track>
        <Slider.Thumb className="kuika-slider__thumb" aria-label="Slider thumb" />
      </Slider.Root>
      {showInput && (
        <KuikaInput
          className="kuika-slider__input"
          type="number"
          size={size}
          value={current}
          min={min}
          max={max}
          precision={precision}
          rightUnit={unit}
          disabled={disabled}
          onChange={handleInputChange}
          data-testid={dataTestId ? `${dataTestId}-input` : undefined}
        />
      )}
    </div>
  );
};

export default KuikaSlider;
